import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import {
  fetchPaymentLinks,
  createPaymentLink,
  updatePaymentLink,
  deletePaymentLink,
  PaymentLink,
} from '../../store/slices/merchantSlice';

const PaymentLinksPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { paymentLinks, loadingPaymentLinks } = useSelector((state: RootState) => state.merchant);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    currency: 'USD',
    description: '',
    displayBankDetails: false,
    allowMalpayPayment: true,
    allowCustomAmount: false,
    showSenderNameField: false,
    showSenderDescriptionField: false,
    storeEnabled: false,
  });

  useEffect(() => {
    dispatch(fetchPaymentLinks());
  }, [dispatch]);

  const handleOpenDialog = (link?: PaymentLink) => {
    if (link) {
      setIsEditing(true);
      setEditingId(link.id);
      setFormData({
        name: link.name,
        amount: link.amount?.toString() || '',
        currency: link.currency,
        description: link.description || '',
        displayBankDetails: link.displayBankDetails,
        allowMalpayPayment: link.allowMalpayPayment,
        allowCustomAmount: link.allowCustomAmount,
        showSenderNameField: link.showSenderNameField,
        showSenderDescriptionField: link.showSenderDescriptionField,
        storeEnabled: link.storeEnabled,
      });
    } else {
      setIsEditing(false);
      setEditingId(null);
      setFormData({
        name: '',
        amount: '',
        currency: 'USD',
        description: '',
        displayBankDetails: false,
        allowMalpayPayment: true,
        allowCustomAmount: false,
        showSenderNameField: false,
        showSenderDescriptionField: false,
        storeEnabled: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSave = async () => {
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      alert('Please enter a name for the payment link.');
      return;
    }
    const parsedAmount = formData.amount ? parseFloat(formData.amount) : undefined;
    if (parsedAmount !== undefined && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
      alert('Amount must be a positive number.');
      return;
    }
    const submitData = {
      name: trimmedName,
      amount: parsedAmount,
      currency: formData.currency,
      description: formData.description,
      displayBankDetails: formData.displayBankDetails,
      allowMalpayPayment: formData.allowMalpayPayment,
      allowCustomAmount: formData.allowCustomAmount,
      showSenderNameField: formData.showSenderNameField,
      showSenderDescriptionField: formData.showSenderDescriptionField,
      storeEnabled: formData.storeEnabled,
    };

    if (isEditing && editingId) {
      await dispatch(updatePaymentLink({ id: editingId, data: submitData }));
    } else {
      await dispatch(createPaymentLink(submitData));
    }
    handleCloseDialog();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this payment link?')) {
      await dispatch(deletePaymentLink(id));
    }
  };

  const handleCopyUrl = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loadingPaymentLinks) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{
            fontWeight: 700,
            color: '#202647',
            fontSize: { xs: '1.5rem', md: '2rem' },
          }}>
            Payment Links
          </Typography>
          <Typography variant="body2" sx={{ color: '#7F839C', mt: 0.5 }}>
            Create and manage your payment collection links
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Create Payment Link
        </Button>
      </Box>

      {/* Payment Links List */}
      {paymentLinks.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 6 }}>
          <Typography variant="body1" sx={{ color: '#7F839C', mb: 2 }}>
            No payment links yet. Create one to start collecting payments.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create First Payment Link
          </Button>
        </Card>
      ) : (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(32, 38, 71, 0.06)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F8F8FC' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Transactions</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentLinks.map((link) => (
                  <TableRow key={link.id} sx={{ '&:hover': { backgroundColor: '#F8F8FC' } }}>
                    <TableCell sx={{ color: '#565972', fontWeight: 500 }}>
                      {link.name}
                    </TableCell>
                    <TableCell sx={{ color: '#565972' }}>
                      {link.amount
                        ? `${link.currency} ${link.amount.toLocaleString()}`
                        : 'Custom'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={link.status}
                        color={link.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#565972' }}>
                      {link.totalTransactions}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyUrl(link.url, link.id)}
                        title="Copy Link"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(link)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(link.id)}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Mobile Card View */}
      <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {paymentLinks.map((link) => (
          <Grid size={{ xs: 12 }} key={link.id}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
                    {link.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7F839C', mt: 0.5 }}>
                    {link.description}
                  </Typography>
                </Box>

                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ color: '#7F839C' }}>Amount</Typography>
                    <Typography sx={{ fontWeight: 600, color: '#202647' }}>
                      {link.amount ? `${link.currency} ${link.amount.toLocaleString()}` : 'Custom'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ color: '#7F839C' }}>Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={link.status}
                        color={link.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Typography variant="caption" sx={{ color: '#7F839C', display: 'block', mb: 1.5 }}>
                  Transactions: {link.totalTransactions}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={() => handleCopyUrl(link.url, link.id)}
                  >
                    {copiedId === link.id ? 'Copied!' : 'Copy'}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(link)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(link.id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          fontWeight: 700,
          color: '#202647',
          bgcolor: '#F8F8FC',
          borderBottom: '1px solid #E8E8F0',
        }}>
          {isEditing ? 'Edit Payment Link' : 'Create Payment Link'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Link Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Product A, Service B"
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Amount (Optional)"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Leave empty for custom"
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    label="Currency"
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="NGN">NGN</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Description (Optional)"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={2}
              placeholder="Add details about what this link is for"
            />

            <Box sx={{ borderTop: '1px solid #E8E8F0', pt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202647', mb: 1.5 }}>
                Payment Options
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="allowMalpayPayment"
                      checked={formData.allowMalpayPayment}
                      onChange={handleInputChange}
                    />
                  }
                  label="Allow MalPay Payment"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="allowCustomAmount"
                      checked={formData.allowCustomAmount}
                      onChange={handleInputChange}
                    />
                  }
                  label="Allow Custom Amount"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="displayBankDetails"
                      checked={formData.displayBankDetails}
                      onChange={handleInputChange}
                    />
                  }
                  label="Display Bank Details"
                />
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #E8E8F0', pt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202647', mb: 1.5 }}>
                Payer Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="showSenderNameField"
                      checked={formData.showSenderNameField}
                      onChange={handleInputChange}
                    />
                  }
                  label="Request Sender Name"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="showSenderDescriptionField"
                      checked={formData.showSenderDescriptionField}
                      onChange={handleInputChange}
                    />
                  }
                  label="Request Sender Description"
                />
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  name="storeEnabled"
                  checked={formData.storeEnabled}
                  onChange={handleInputChange}
                />
              }
              label="Enable Store on this Link"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E8E8F0' }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained">
            {isEditing ? 'Update' : 'Create'} Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentLinksPage;
